import React, { ReactElement, useState } from "react";
import { RiEyeCloseLine } from "@remixicon/react";
import useKeyboardShortcuts from "../../../contexts/KeyboardShortcutContext";
import { AssignmentType, Ticket } from "../../../contexts/selection-state/selection-state";
import useSelectionState from "../../../contexts/selection-state/SelectionStateContext";
import { ChatStatus, useIgnoreMutation } from "../../../__generated__/api";
import TimedToast from "../../timed-toast/TimedToast";
import {
    ActionContext,
    ActionHandler, ActionType, HANDLER_INTERFACE, useRegisterAction,
} from "../../../contexts/ticket-state/action-registry";
import { useTicketState } from "../../../contexts/ticket-state";

interface Props {
    refreshData?: () => void
}
export default function Ignore(props: Props): ReactElement | null {
    const selectionState = useSelectionState();
    const [sendIgnore] = useIgnoreMutation();

    const [lastIgnored, setLastIgnored] = useState<Ticket[]>([]);
    const [openedId, setOpenedId] = useState<string>();
    const [actionHandler, setActionHandler] = useState<ActionHandler>();
    const ticketContext = useTicketState();

    const ignore = (tickets?: Ticket[]) => {
        const selection = tickets
            ?? ticketContext?.selectionState.current?.toIdList("chat")
            ?? selectionState
                .getSelection()
                .filter(ticket => ticket.status === ChatStatus.ChatRequested);
        const selectedIds = selection.map(row => row.id) as string[];

        setOpenedId(selectionState.currentId && selectedIds.includes(selectionState.currentId)
            ? selectionState.currentId
            : undefined);

        selectionState.closeCurrent();
        selectionState.setChatStatuses(selectedIds, ChatStatus.Ignored);
        selectionState.hide(...selectedIds);

        setLastIgnored(selection);
    };

    const doIgnore = async (tickets?: Ticket[], handler?: ActionHandler) => {
        const selectedIds = (tickets ?? lastIgnored).toIdList("id");

        const ignoreHandler = handler ?? actionHandler;
        ignoreHandler?.items.forEach(row => {
            row.setStatusLocal(ChatStatus.Ignored);
            row.closeIfOpen();
        });
        ignoreHandler?.done();

        if (selectedIds.isNotEmpty) {
            await sendIgnore({
                variables: {
                    ids: selectedIds,
                },
            });
        }

        setLastIgnored([]);
        setOpenedId(undefined);
    };

    const onCancel = () => {
        lastIgnored.forEach(ticket => {
            selectionState?.setChatStatus(
                ticket.id,
                ticket.status,
                ticket.isAssignedToCurrentUser ? AssignmentType.CURRENT_USER : AssignmentType.NONE,
            );
            selectionState?.unhide(ticket.id as string);
        });
        if (openedId) {
            selectionState?.setOpened(openedId);
        }
        setLastIgnored([]);
        setOpenedId(undefined);
        props.refreshData?.();
        actionHandler?.rollback();
    };

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "shift+!",
            context: "default",
            handler: () => {
                ignore();
                setActionHandler(ticketContext?.selectionState.createActionHandler(ActionContext.BULK_ACTION));
            },
        });
        shortcuts.on({
            shortcut: "k+i",
            context: "default",
            description: "Ignore the conversation",
            group: "Conversations",
            handler: () => {
                ignore();
                setActionHandler(ticketContext?.selectionState.createActionHandler(ActionContext.BULK_ACTION));
            },
        });
    });

    useRegisterAction({
        createBulkActionButton(items) {
            return items.some(row => row.status === ChatStatus.ChatRequested) ? {
                text: "Ignore",
                icon: <RiEyeCloseLine />,
                variant: "ghost-gray",
            } : undefined;
        },
        handleBulkAction(handler) {
            const tickets = handler.items.map(row => row.chat);
            if (handler.context === ActionContext.TICKET_ACTION) {
                handler.items.find(item => item.closeIfOpen());
            }
            ignore(tickets);
            setActionHandler(handler);
        },
    }, ActionType.IGNORE);

    selectionState?.registerIgnoreHandler(tickets => {
        ignore(tickets);
    });

    return (
        <TimedToast
            // key={lastIgnored.toIdList("id").join()}
            canCancel
            shouldShow={lastIgnored.length > 0}
            title={`${lastIgnored.length} items ignored.`}
            seconds={5}
            onClose={doIgnore}
            onCancel={onCancel}
        >
            <div>
                You ignored
                {" "}
                {lastIgnored.length}
                {" "}
                items.
                {" "}
            </div>
        </TimedToast>
    );
}

HANDLER_INTERFACE.register(Ignore);

Ignore.defaultProps = {
    refreshData: () => {
        // nothing
    },
};
